import { Chart, useChart } from '@chakra-ui/charts';
import { Box, Skeleton, Stack } from '@chakra-ui/react';
import type React from 'react';
import { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { formatChartNumber } from '@/utils';
import { FaceliftScrollContainer } from '../../Atoms/FaceliftScrollContainer';
import FaceliftText from '../../Atoms/FaceliftText/FaceliftText';
import Show from '../../Molecules/Show';
import { useColorMode } from '../../ui/color-mode-utils';
import { colors } from '../../ui/theme';
import type {
  ChartDataPoint,
  ChartSeries,
  StackedBarChartProps,
} from './StackedBarChart.types';

interface TooltipPayloadItem {
  name: string;
  value: number;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  disabledSeries: Set<string>;
}

// Custom tooltip component that excludes disabled series
const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  disabledSeries,
}) => {
  if (!active || !payload) return null;

  // Filter out disabled series from the tooltip
  const filteredPayload = payload.filter((entry) => {
    return !disabledSeries.has(String(entry.name));
  });

  if (filteredPayload.length === 0) return null;

  return (
    <Box
      p={3}
      borderRadius="md"
      borderWidth="1px"
      boxShadow="lg"
      _dark={{ bg: 'darkModeBgColor', borderColor: 'gray.600' }}
      _light={{ bg: 'white', borderColor: 'gray.200' }}
    >
      <FaceliftText
        fontSize="sm"
        fontWeight="semibold"
        mb={2}
        _dark={{ color: 'white' }}
        _light={{ color: 'gray.800' }}
      >
        {label}
      </FaceliftText>
      <Stack gap={1}>
        {filteredPayload.map((entry) => (
          <Stack
            key={`${label}-${entry.name}`}
            direction="row"
            align="center"
            gap={2}
          >
            <Box
              width={2.5}
              height={2.5}
              borderRadius="full"
              bg={entry.color}
            />
            <FaceliftText fontSize="xs" _dark={{ color: 'gray.300' }} _light={{ color: 'gray.600' }}>
              {entry.name}: {entry.value?.toLocaleString()}
            </FaceliftText>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const StackedBarChart: React.FC<StackedBarChartProps> = ({
  data: propData,
  title = '',
  isLoading = false,
  ...rest
}) => {
  // Default color palette for series
  const defaultColors = [
    'axolotlBlue',
    'nvisionPurple',
    'imbedGray',
    'brand.600',
  ];

  // Transform the nested data structure into flat format for Recharts
  const { transformedData, seriesConfig, calculatedDomain, calculatedTicks } =
    useMemo(() => {
      if (!propData || propData.length === 0) {
        return {
          transformedData: [],
          seriesConfig: [],
          calculatedDomain: [0, 'auto'] as [number, string],
          calculatedTicks: undefined,
        };
      }

      // Extract all unique titles (series names) from the data
      const titlesSet = new Set<string>();
      propData.forEach((category) => {
        category.data.forEach((item) => {
          titlesSet.add(item.title);
        });
      });

      const titles = Array.from(titlesSet);

      // Create series configuration with colors
      const series: ChartSeries[] = titles.map((title, index) => ({
        name: title,
        color: defaultColors[index % defaultColors.length],
      }));

      // Transform data into flat structure and calculate max value
      let maxStackedValue = 0;
      const flatData: ChartDataPoint[] = propData.map((category) => {
        const dataPoint: ChartDataPoint = { category: category.category };
        let categoryTotal = 0;

        // Add each series value to the data point
        category.data.forEach((item) => {
          dataPoint[item.title] = item.value;
          categoryTotal += item.value;
        });

        // Fill in missing series with 0
        titles.forEach((title) => {
          if (!(title in dataPoint)) {
            dataPoint[title] = 0;
          }
        });

        maxStackedValue = Math.max(maxStackedValue, categoryTotal);
        return dataPoint;
      });

      // Calculate domain and ticks
      // Round up to nearest nice number
      const roundToNice = (num: number): number => {
        const magnitude = 10 ** Math.floor(Math.log10(num));
        const normalized = num / magnitude;
        let nice: number;

        if (normalized <= 1) nice = 1;
        else if (normalized <= 2) nice = 2;
        else if (normalized <= 5) nice = 5;
        else nice = 10;

        return nice * magnitude;
      };

      const maxValue = roundToNice(maxStackedValue * 1.1); // Add 10% padding
      const tickInterval = maxValue / 4; // 4 intervals = 5 ticks including 0
      const ticks = Array.from({ length: 5 }, (_, i) =>
        Math.round(i * tickInterval),
      );

      return {
        transformedData: flatData,
        seriesConfig: series,
        calculatedDomain: [0, maxValue] as [number, number],
        calculatedTicks: ticks,
      };
    }, [propData]);

  // State to track disabled series
  const [disabledSeries, setDisabledSeries] = useState<Set<string>>(new Set());

  // Use Chakra's useChart hook
  const chart = useChart({
    data: transformedData,
    series: seriesConfig,
  });

  // Get the color for a series (disabled series use color from above, or transparent if at top)
  const getSeriesColor = (seriesIndex: number) => {
    const currentSeries = seriesConfig?.[seriesIndex];
    if (!currentSeries) return '';

    const isDisabled = disabledSeries.has(String(currentSeries.name));

    if (isDisabled && seriesConfig) {
      // Find the first non-disabled series above this one
      let aboveIndex = seriesIndex + 1;
      while (
        aboveIndex < seriesConfig.length &&
        disabledSeries.has(String(seriesConfig[aboveIndex].name))
      ) {
        aboveIndex++;
      }

      if (aboveIndex < seriesConfig.length) {
        // Use the color from the series above (it expands down)
        return chart.color(seriesConfig[aboveIndex].color);
      }

      // If no series above, this is at the top - make it transparent
      return 'transparent';
    }

    // Use own color
    return chart.color(currentSeries.color);
  };

  // Color mode
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  // Check if all series are disabled
  const allSeriesDisabled =
    seriesConfig?.every((series) => disabledSeries.has(String(series.name))) ||
    false;

  // Toggle series visibility
  const handleLegendClick = (seriesName: string) => {
    setDisabledSeries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(seriesName)) {
        newSet.delete(seriesName);
      } else {
        newSet.add(seriesName);
      }
      return newSet;
    });
  };

  // Render custom legend separately (outside of chart)
  const renderCustomLegend = () => {
    const allSeries = seriesConfig || [];

    return (
      <Box
        width="100%"
        minHeight="19px"
        mt={1}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Stack
          direction="row"
          justify="center"
          align="center"
          gap={3}
          opacity={1}
          flexWrap="wrap"
        >
          {allSeries.map((series, index) => {
            const seriesName = String(series.name);
            const isDisabled = disabledSeries.has(seriesName);
            // Find the color from the chart
            const color = chart.color(series.color);

            return (
              <Stack
                key={seriesName || `legend-${index}`}
                direction="row"
                align="center"
                gap={1.5}
                flexShrink={0}
                cursor="pointer"
                onClick={() => handleLegendClick(seriesName)}
                opacity={isDisabled ? 0.4 : 1}
                transition="opacity 0.2s"
                _hover={{ opacity: isDisabled ? 0.6 : 0.8 }}
              >
                <Box width={2.5} height={2.5} borderRadius="full" bg={color} />
                <FaceliftText
                  fontSize="2xs"
                  fontWeight="medium"
                  lineHeight="tight"
                  _dark={{ color: 'gray.200' }}
                  _light={{ color: 'black' }}
                  whiteSpace="nowrap"
                  textDecoration={isDisabled ? 'line-through' : 'none'}
                >
                  {seriesName}
                </FaceliftText>
              </Stack>
            );
          })}
        </Stack>
      </Box>
    );
  };

  return (
    <>
      <Show when={isLoading}>
        <Box
          width="100%"
          height="350px"
          borderRadius="6px"
          borderWidth="1px"
          borderColor={'gray.400'}
          p={4}
          pl={2}
          _dark={{ bg: 'darkModeBgColor' }}
          _light={{ bg: 'white' }}
          overflow="hidden"
          display="flex"
          flexDirection="column"
          {...rest}
        >
          <Skeleton height="24px" width="60%" maxW="300px" mb={2} ml={8} _dark={{ bg: 'gray.700' }} _light={{ bg: 'gray.200' }} />
          <Box flex="1" px={2} py={2} display="flex" alignItems="flex-end" justifyContent="center" gap={6} height="240px">
            {Array.from({ length: 8 }).map((_, i) => {
              const total = 40 + (i % 5) * 10; // total bar height percentage
              const patterns = [
                [0.5, 0.3, 0.2],
                [0.6, 0.4],
                [0.45, 0.35, 0.2],
                [0.35, 0.35, 0.3],
              ];
              const fractions = patterns[i % patterns.length];
              const heights = fractions.map((f) => `${Math.round(total * f)}%`);
              return (
                <Box
                  // biome-ignore lint/suspicious/noArrayIndexKey: Temporary skeleton loader
                  key={i}
                  width="70px"
                  height="100%"
                  display="flex"
                  justifyContent="center"
                  alignItems="flex-end"
                >
                  <Box
                    width="100%"
                    height={`${total}%`
                    }
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    gap={1}
                  >
                    {heights.map((h, idx) => (
                      <Skeleton
                        // biome-ignore lint/suspicious/noArrayIndexKey: Temporary skeleton loader
                        key={idx}
                        height={h}
                        width="100%"
                        borderRadius={idx === heights.length - 1 ? '4px 4px 0 0' : '0'}
                        _dark={{ bg: 'gray.700' }}
                        _light={{ bg: 'gray.200' }}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Stack direction="row" justify="center" gap={3} mt={2} flexWrap="wrap">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton
                // biome-ignore lint/suspicious/noArrayIndexKey: Temporary skeleton loader
                key={i}
                height="16px"
                width="100px"
                _dark={{ bg: 'gray.700' }}
                _light={{ bg: 'gray.200' }}
              />
            ))}
          </Stack>
        </Box>
      </Show>

      <Show when={!isLoading}>
        <Box
          width="100%"
          height="350px"
          borderRadius="6px"
          borderWidth="1px"
          borderColor={'gray.400'}
          p={4}
          pl={2}
          _dark={{ bg: 'darkModeBgColor' }}
          _light={{ bg: 'white' }}
          overflow="hidden"
          display="flex"
          flexDirection="column"
          {...rest}
        >
          <Show when={!!title}>
            <FaceliftText
              fontSize="md"
              fontWeight="semibold"
              mb={2}
              mt={1}
              ml={4}
              _dark={{ color: 'white' }}
              _light={{ color: 'black' }}
            >
              {title}
            </FaceliftText>
          </Show>
          <Box position="relative" height="calc(100% - 80px)">
            <Show when={allSeriesDisabled}>
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                zIndex={10}
              >
                <FaceliftText
                  fontSize="md"
                  fontWeight="semibold"
                  _dark={{ color: 'gray.400' }}
                  _light={{ color: 'gray.500' }}
                >
                  No data found
                </FaceliftText>
              </Box>
            </Show>
            <FaceliftScrollContainer
              height="100%"
              minWidth="1300px"
              pb={2}
            >
              <Box position="relative" height="100%" width="100%">
                <Chart.Root height="100%" width="100%" chart={chart}>
                  <BarChart
                    data={chart.data}
                    margin={{ top: 20, right: 20, left: 2, bottom: 12 }}
                    barCategoryGap="20%"
                    width={1300}
                    height={240}
                  >
                    <Show when={!allSeriesDisabled}>
                      <CartesianGrid
                        strokeDasharray="0"
                        vertical={false}
                        stroke={colors.robotGrey}
                      />
                    </Show>
                    <Show when={!allSeriesDisabled}>
                      <XAxis
                        dataKey={chart.key('category')}
                        axisLine={{
                          stroke: isDark ? colors.lightGray : colors.black,
                          strokeWidth: 1,
                        }}
                        tickLine={false}
                        tick={{
                          style: {
                            fill: isDark ? colors.lightGray : colors.black,
                            fontSize: '12px',
                            fontWeight: 500,
                            lineHeight: '100%',
                            letterSpacing: '1%',
                            textAlign: 'center',
                            textTransform: 'capitalize',
                          },
                        }}
                        dy={10}
                      />
                    </Show>
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={
                        !allSeriesDisabled
                          ? {
                            style: {
                              fill: isDark ? colors.lightGray : colors.black,
                              fontSize: '10px',
                              fontWeight: 500,
                              lineHeight: '100%',
                              letterSpacing: '1%',
                              textAlign: 'right',
                              textTransform: 'capitalize',
                            },
                          }
                          : false
                      }
                      tickFormatter={
                        !allSeriesDisabled ? formatChartNumber : undefined
                      }
                      domain={
                        !allSeriesDisabled
                          ? calculatedDomain
                          : [0, 'auto']
                      }
                      ticks={!allSeriesDisabled ? calculatedTicks : []}
                    />
                    <Show when={!allSeriesDisabled}>
                      <Tooltip
                        cursor={false}
                        animationDuration={100}
                        content={
                          <CustomTooltip
                            disabledSeries={disabledSeries}
                          />
                        }
                      />
                    </Show>
                    {chart.series.map((item, index: number) => (
                      <Bar
                        key={String(item.name)}
                        dataKey={chart.key(String(item.name))}
                        stackId="a"
                        fill={getSeriesColor(index)}
                        radius={
                          index === chart.series.length - 1
                            ? [2, 2, 0, 0]
                            : [0, 0, 0, 0]
                        }
                      />
                    ))}
                  </BarChart>
                </Chart.Root>
              </Box>
            </FaceliftScrollContainer>
          </Box>
          {renderCustomLegend()}
        </Box>
      </Show>
    </>
  );
};

export default StackedBarChart;
